import type { TrackerSet } from '@/domain/content.types';
import { listSets } from '@/content/registry';
import { useT } from '@/i18n/useT';
import { useSettings, viewFor } from '@/state/settingsStore';
import { ProfileMenu } from '@/components/profile/ProfileMenu';

interface TopBarProps {
  set: TrackerSet;
  onOpenShare: () => void;
  onOpenSettings: () => void;
}

export function TopBar({ set, onOpenShare, onOpenSettings }: TopBarProps) {
  const { t, lang } = useT();
  const setLang = useSettings((s) => s.setLang);
  const setActiveSetId = useSettings((s) => s.setActiveSetId);
  const views = useSettings((s) => s.views);
  const setView = useSettings((s) => s.setView);
  const view = viewFor(views, set.id, set.map !== undefined);
  const sets = listSets();

  return (
    <header className="flex items-center gap-2 border-b border-line bg-surface px-2 py-1.5">
      <span aria-hidden="true" className="text-base leading-none">
        🐾
      </span>
      <h1 className="shrink-0 font-display text-[13px] leading-5 font-bold tracking-tight text-pip">
        {t('app.name')}
      </h1>

      {/* A single set needs no picker; it appears as soon as there are two. */}
      {sets.length > 1 ? (
        <select
          value={set.id}
          onChange={(e) => setActiveSetId(e.target.value)}
          className="min-w-0 max-w-40 truncate rounded-xs border border-line bg-surface-2 px-1.5 py-0.5 text-[12px] leading-4 text-text-dim focus:border-pip-dim focus:outline-none md:max-w-64"
        >
          {sets.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title[lang]}
            </option>
          ))}
        </select>
      ) : (
        <span className="min-w-0 truncate text-[12px] leading-4 text-text-dim">
          {set.title[lang]}
        </span>
      )}

      <div className="ml-auto flex shrink-0 items-center gap-1.5">
        <div
          className="flex overflow-hidden rounded-xs border border-line"
          role="group"
          aria-label={t('settings.language')}
        >
          {(['en', 'ru'] as const).map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => setLang(code)}
              aria-pressed={lang === code}
              className={`px-1.5 py-1 font-display text-[11px] leading-4 uppercase transition-colors ${
                lang === code ? 'bg-pip text-bg' : 'bg-surface-2 text-text-faint hover:text-pip'
              }`}
            >
              {code}
            </button>
          ))}
        </div>

        {set.map ? (
          <div className="flex overflow-hidden rounded-xs border border-line" role="group" aria-label={t('view.map')}>
            {(['list', 'map'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setView(set.id, mode)}
                aria-pressed={view === mode}
                title={t(`view.${mode}`)}
                className={`px-1.5 py-1 text-[11px] leading-4 transition-colors ${
                  view === mode ? 'bg-pip text-bg' : 'bg-surface-2 text-text-faint hover:text-pip'
                }`}
              >
                <span aria-hidden="true">{mode === 'list' ? '☰' : '🗺'}</span>
                <span className="sr-only">{t(`view.${mode}`)}</span>
              </button>
            ))}
          </div>
        ) : null}

        <ProfileMenu />

        <button
          type="button"
          onClick={onOpenShare}
          aria-label={t('share.title')}
          title={t('share.title')}
          className="rounded-xs border border-line bg-surface-2 px-1.5 py-1 text-[12px] leading-4 text-text-faint hover:border-pip-dim hover:text-pip"
        >
          📷
        </button>

        <button
          type="button"
          onClick={onOpenSettings}
          aria-label={t('settings.title')}
          title={t('settings.title')}
          className="rounded-xs border border-line bg-surface-2 px-1.5 py-1 text-[12px] leading-4 text-text-faint hover:border-pip-dim hover:text-pip"
        >
          ⚙
        </button>
      </div>
    </header>
  );
}
